import { useEffect, useId, useMemo, useRef } from 'react';
import GroupChannelHandler from '@sendbird/uikit-react/handlers/GroupChannelHandler';
import useSendbirdStateContext from '@sendbird/uikit-react/useSendbirdStateContext';
import { isObjectEmpty } from '../../helpers/common';
import { SendbirdUIKitProvider } from './SendbirdUIKitProvider';
import {
  ChannelMentionFlagMap,
  fetchChannelMentionFlags,
} from '../../utils/sendbird/channelMentionStatus';
import { defaultSendbirdGroupChannelUikitOptions } from '../../constants/sendbirdConstants';

export type SendbirdMentionSyncRow = {
  channelUrl: string;
  isNewModelChannel?: boolean;
};

type SendbirdMentionSyncHookProps = {
  channelUrls: string[];
  selectedChannelUrl?: string;
  onFlagsUpdate: (flags: ChannelMentionFlagMap) => void;
};

function mergeMentionFlags(
  previous: ChannelMentionFlagMap,
  incoming: ChannelMentionFlagMap,
): ChannelMentionFlagMap | null {
  let changed = false;
  const next = { ...previous };
  for (const [url, hasMention] of Object.entries(incoming)) {
    if (previous[url] !== hasMention) {
      next[url] = hasMention;
      changed = true;
    }
  }
  return changed ? next : null;
}

const SendbirdMentionSyncHook = ({
  channelUrls,
  selectedChannelUrl,
  onFlagsUpdate,
}: SendbirdMentionSyncHookProps) => {
  const { stores, config } = useSendbirdStateContext();
  const sdk = stores?.sdkStore?.sdk;
  const connectedUserId = config?.userId ?? '';
  const handlerId = useId();
  const syncInFlightRef = useRef(false);
  const lastFlagsRef = useRef<ChannelMentionFlagMap>({});
  const onFlagsUpdateRef = useRef(onFlagsUpdate);
  onFlagsUpdateRef.current = onFlagsUpdate;

  const channelUrlsKey = useMemo(() => channelUrls.slice().sort().join('|'), [channelUrls]);

  useEffect(() => {
    if (!sdk?.groupChannel || isObjectEmpty(sdk) || !channelUrls.length || !connectedUserId) return;

    let cancelled = false;

    const runSync = async () => {
      if (cancelled || syncInFlightRef.current) return;
      syncInFlightRef.current = true;
      try {
        const flags = await fetchChannelMentionFlags(sdk, channelUrls, selectedChannelUrl);
        if (cancelled) return;
        const merged = mergeMentionFlags(lastFlagsRef.current, flags);
        if (!merged) return;
        lastFlagsRef.current = merged;
        onFlagsUpdateRef.current(merged);
      } finally {
        syncInFlightRef.current = false;
      }
    };

    void runSync();

    const handler = new GroupChannelHandler({
      onMessageReceived: () => {
        void runSync();
      },
    });
    sdk.groupChannel.addGroupChannelHandler(handlerId, handler);

    return () => {
      cancelled = true;
      sdk.groupChannel?.removeGroupChannelHandler(handlerId);
    };
  }, [sdk, channelUrlsKey, connectedUserId, selectedChannelUrl, handlerId]);

  return null;
};

type SendbirdEventMentionSyncProps = {
  rows: SendbirdMentionSyncRow[];
  vendorSendBirdId?: string | null;
  selectedChannelUrl?: string;
  onFlagsUpdate: (flags: ChannelMentionFlagMap) => void;
};

/**
 * NAZ-621: Batch-syncs Sendbird unreadMentionCount onto event chat list rows after connect().
 *
 * Mount only while the event chat panel is closed — parents must not render this alongside
 * an active `SendbirdUIKitProvider` for the same channel (see EventDetail / CorporateEventDetails).
 * Up to two headless providers may mount when both new-model and legacy channel URLs exist.
 */
export const SendbirdEventMentionSync = ({
  rows,
  vendorSendBirdId,
  selectedChannelUrl,
  onFlagsUpdate,
}: SendbirdEventMentionSyncProps) => {
  const newModelUrls = useMemo(
    () =>
      rows
        .filter((row) => row.channelUrl && row.isNewModelChannel !== false)
        .map((row) => row.channelUrl),
    [rows],
  );

  const legacyUrls = useMemo(
    () =>
      rows
        .filter((row) => row.channelUrl && row.isNewModelChannel === false)
        .map((row) => row.channelUrl),
    [rows],
  );

  const uikitOptions = useMemo(
    () => ({
      groupChannel: defaultSendbirdGroupChannelUikitOptions,
    }),
    [],
  );

  return (
    <>
      {newModelUrls.length > 0 ? (
        <SendbirdUIKitProvider isNewModelChannel headless uikitOptions={uikitOptions}>
          <SendbirdMentionSyncHook
            channelUrls={newModelUrls}
            selectedChannelUrl={selectedChannelUrl}
            onFlagsUpdate={onFlagsUpdate}
          />
        </SendbirdUIKitProvider>
      ) : null}
      {legacyUrls.length > 0 && vendorSendBirdId ? (
        <SendbirdUIKitProvider
          isNewModelChannel={false}
          vendorSendBirdId={vendorSendBirdId}
          headless
          uikitOptions={uikitOptions}
        >
          <SendbirdMentionSyncHook
            channelUrls={legacyUrls}
            selectedChannelUrl={selectedChannelUrl}
            onFlagsUpdate={onFlagsUpdate}
          />
        </SendbirdUIKitProvider>
      ) : null}
    </>
  );
};

export default SendbirdEventMentionSync;
